/**
 * SSE event types and payload shapes.
 *
 * Event names must match backend EventType enum in event_bus.py.
 *
 * AIDEV-NOTE: When adding a new event type, update BOTH:
 * 1. Backend: app/api/services/event_bus.py -> EventType enum
 * 2. Frontend: this file (constant + payload type + map entry) +
 *    useRealtimeInvalidation.ts switch cases
 */

import type { InteractionType } from '@models/api';
import type { UUID } from '@models/api/shared';

// ── Event type string literals ────────────────────────────────────────
export const SSE_EVENT_TYPE = {
  // Global feeds & interactions
  QUESTION_CREATED: 'question.created',
  QUESTION_UPDATED: 'question.updated',
  QUESTION_DELETED: 'question.deleted',
  // Asker-addressed: the matcher (re)computed and NEW helpers arrived for a question THIS
  // user asked (async-matching #22). Delivered only to the asker's app-wide stream; drives
  // the arrival banner and refetches the asker's suggested-helpers + match-state caches.
  QUESTION_MATCHES_UPDATED: 'question.matches_updated',
  INTERACTION_CHANGED: 'interaction.created',
  // Per-recipient: a new post landed near the top of THIS user's personalized
  // feed (backend worker, #307). Drives the new-posts pill.
  FEED_NEW_TOP_POST: 'feed.new_top_post',
  // A We Met v2 connection involving this user changed — request received,
  // auto-confirm, confirm, or removal (backend #356). Scoped per-recipient;
  // drives the connections list + pending dot live.
  WE_MET_CHANGED: 'we_met.changed',

  // Notifications & chat
  NOTIFICATION_CREATED: 'notification.created',
  NOTIFICATION_UPDATED: 'notification.updated',
  ROOM_UPDATED: 'room.updated',
  CHAT_MESSAGE: 'chat.message',

  // Communities (spaces)
  SPACE_UPDATED: 'space.updated',
  SPACE_MEMBER_JOINED: 'space.member_joined',
  SPACE_MEMBER_LEFT: 'space.member_left',
  SPACE_MEMBER_STATUS_CHANGED: 'space.member_status_changed',
  SPACE_MEMBER_REMOVED: 'space.member_removed',
  SPACE_TOPIC_CHANGED: 'space.topic_changed',
  // A new question was posted in a community (backend endpoint), scoped to the
  // space's members — one emit per linked space.
  SPACE_QUESTION_CREATED: 'space.question_created',
  // A calendar sync job for this space finished (backend worker). Content-free poke to the
  // space's admins/creator: the handler refetches the calendar list, which carries the truth.
  CALENDAR_SYNC_CHANGED: 'calendar.sync_changed',

  // Events (calendar)
  EVENT_CREATED: 'event.created',
  EVENT_UPDATED: 'event.updated',
  EVENT_DELETED: 'event.deleted',
  // Server-side live transitions (Event Clock, spec §2.4): a backend cron flips an
  // event's phase and emits these for instant live-surface flips. Wire strings match
  // the backend EventType enum exactly; the client clock still renders phases from dates.
  EVENT_WENT_LIVE: 'event.went_live',
  EVENT_ENDED: 'event.ended',

  // Users
  // A user's public identity changed (profile edit or account erasure).
  // Broadcast by design; IDs-only payload; handlers invalidate rather than
  // patch (8 divergent embedded-user shapes make a generic patcher a
  // separate project, see docs/sse-realtime/ROADMAP.md P5).
  USER_UPDATED: 'user.updated',

  // Helper feed
  HELPER_MATCH_CREATED: 'helper_match.created',

  // Meta
  FULL_SYNC: 'full-sync',
  CONNECTED: 'connected',
} as const;

export type SSEEventType = (typeof SSE_EVENT_TYPE)[keyof typeof SSE_EVENT_TYPE];

// ── Per-event payload shapes ──────────────────────────────────────────
// Fields are typed as optional to honour the runtime reality: payloads
// can arrive incomplete (oversize-truncated, partial backend regression,
// schema drift). Existing handlers already gate on field presence; the
// optionals match that defensive shape so the type doesn't lie.
//
// Only fields the existing handlers actually read are typed. Backend may
// send richer payloads; consumers can widen these as new fields land in
// handler logic.

interface QuestionPayload {
  questionId?: UUID;
}

interface QuestionMatchesUpdatedPayload {
  /** The asker's question whose match set changed. camelCase per the backend's SSE payload
   *  convention (questionId / actorUserId / spaceId / ... are all hand-written camelCase). */
  questionId?: UUID;
  /** Newly-matched helper user ids (the delta that triggered this emit). */
  newHelperUserIds?: UUID[];
  /** The question's slug when the backend can resolve it (nullable, #22). Lets the arrival
   *  banner suppress on the slug-routed detail screen and route the tap to the canonical slug
   *  URL even when the entity cache is cold. Absent on older backends -> fall back to the
   *  cached entity slug. */
  questionSlug?: string | null;
  /** Display data for the arrivals, 1:1 (ids, sorted order) with `newHelperUserIds` (backend
   *  #691) - the matcher holds these rows at publish time, so the arrival banner can name a
   *  genuinely NEW helper that is by definition not yet in the pre-arrival suggested-helpers
   *  cache. `[]` when the backend masks display data (anonymity rules) or enrichment fails,
   *  while `newHelperUserIds` still carries ids. Absent (older backend) and `[]` (masked)
   *  are distinct only in intent here - both fall through to the cache-filtered banner path. */
  newHelpers?: { id: UUID; fullName: string; profilePicture: string | null }[];
}

interface InteractionPayload {
  questionId?: UUID;
  /** Which interaction changed (backend interactions.py). Typed as the literal union rather
   *  than a bare string so the handler's `=== InteractionType.me_too` comparisons are
   *  compile-time checked; wire values outside the union are still handled defensively at
   *  runtime. Used to scope the questionInteractions roster refetch to me_too/i_can_help — the
   *  only types whose detail-tab rosters change. */
  interactionType?: InteractionType;
  /** Whether the interaction was added or removed. The backend emits INTERACTION_CHANGED
   *  for BOTH create and delete/toggle-off, so this is the only add-vs-remove discriminator;
   *  paired with actorUserId to reconcile the viewer's own flag across their tabs (webapp#931). */
  action?: 'created' | 'deleted';
  /** The acting user's id (backend#931). The actor always receives their own echo, so a
   *  receiver compares this to its currentUserId to recognise its OWN action and reconcile
   *  the per-viewer user_has_* flag that a broadcast counts payload can't carry. */
  actorUserId?: UUID;
  /** Fresh interaction counts (data event, backend #347) so receivers patch caches in-place
   *  instead of refetching. All-three-or-none: the backend omits the whole object only when
   *  its post-commit count read fails, and the handler falls back to invalidation then. */
  counts?: {
    uplifts_count?: number;
    me_too_count?: number;
    i_can_help_count?: number;
  };
}

interface FeedNewTopPostPayload {
  questionId?: UUID;
}

interface WeMetChangedPayload {
  /** The user who triggered the change (the viewer's counterpart). */
  actorUserId?: UUID;
}

interface SpaceQuestionCreatedPayload {
  spaceId?: UUID;
  questionId?: UUID;
}

interface ChatMessagePayload {
  roomId?: UUID;
  messageId?: UUID;
  // `null` when the backend masks an anonymous sender (backend #525); `undefined`
  // only when the field is genuinely dropped. The handler distinguishes the two:
  // null fast-paths (masking), undefined alarms (regression).
  senderId?: UUID | null;
  content?: string;
  createdAt?: string;
  questionId?: UUID | null;
  /** Set to `true` when backend truncates an oversize payload (see backend docs/SSE_EVENTS.md). */
  _truncated?: boolean;
}

interface RoomUpdatedPayload {
  roomId?: UUID;
}

interface EventEntityPayload {
  eventId?: UUID;
  spaceId?: UUID;
  /** The user whose Going/un-Going action triggered this emit (backend PR #600, participation
   *  emits only). The actor always receives their own echo, so a receiver compares this to its
   *  currentUserId to recognise its OWN action and flip `is_going` on that user's OTHER open
   *  tabs/devices. Mirrors InteractionPayload's actorUserId (webapp#931). */
  actorUserId?: UUID;
  /** Which participation transition fired this emit (participation emits only). Paired with
   *  `actorUserId` to reconcile the acting viewer's own `is_going` flag; a receiver that is NOT
   *  the actor uses `going_count` alone and ignores this field. */
  action?: 'going' | 'ungoing';
  /** Absolute post-commit Going-set count (backend PR #600), read via the same helper
   *  `EventRead.going_count` uses. PRESENCE is the participation-vs-content-edit discriminator
   *  for EVENT_UPDATED: present -> patch this event's caches in place instead of refetching;
   *  absent -> the content-edit invalidation path for event caches. The degrade case (an
   *  actorUserId+action emit whose backend count read failed, so going_count is absent)
   *  follows that same path but additionally reconciles the event presence feed, since a
   *  real going change alters its authorship (see the handler's degrade note). */
  going_count?: number;
  /** Approved cross-post space ids. Lets the actor's own echo reconcile the Space>Going tab
   *  of every space the event is linked to, mirroring useToggleGoingEvent's primary+linked
   *  sweep. Only participation emits carry it (backend #600/#620 sends [] when the event has
   *  no cross-posts); it is ABSENT on content-edit/created emits and on older backends, so
   *  consumers must treat undefined and [] the same way. */
  linkedSpaceIds?: UUID[];
}

/** Server-side live-transition events (Event Clock, spec §2.4/§3.1). The backend's
 *  minute cron flips an event's phase and emits `event.went_live` / `event.ended`
 *  carrying only the event's id + its parent space id — no participation fields
 *  (contrast EventEntityPayload). The client clock still derives phases from dates,
 *  so these only drive the minimal live-surface invalidation in useRealtimeInvalidation.
 *  Both fields optional per the registry convention (payloads can arrive truncated). */
interface EventClockPayload {
  eventId?: UUID;
  spaceId?: UUID;
}

interface SpaceMemberPayload {
  spaceId?: UUID;
  userId?: UUID;
}

interface SpaceTopicChangedPayload {
  /** Space UUID — used to invalidate UUID-keyed caches like
   *  `useSpaceTopics(spaceId)` and `questionsSpaceKey(spaceId)`. */
  spaceId?: UUID;
  /** Space slug — used to invalidate slug-keyed caches that consumers
   *  built with the slug rather than the UUID. */
  identifier?: string;
}

interface CalendarSyncChangedPayload {
  /** Space UUID. The handler doesn't read it: the calendar list is keyed by slug-or-id, so it
   *  invalidates the whole family. Typed for parity with the backend payload. */
  spaceId: UUID;
}

interface UserEntityPayload {
  /** The user whose identity changed. Used for the targeted profile-page
   *  invalidation and the own-identity reconcile; the family-wide sweeps are
   *  user-agnostic and run even when this field is absent. */
  userId?: UUID;
}

interface HelperMatchPayload {
  /** UUID of the helper the match was created for (the SSE recipient).
   *  The handler doesn't read it (a `helper_match.created` event just
   *  triggers a helper-feed family-root invalidate). Typed for parity with
   *  the backend payload and future consumers. */
  userId?: UUID;
}

/** Map each event type to its expected payload shape. Single source of
 *  truth: adding a new event type forces a corresponding payload entry. */
export interface SSEEventDataMap {
  [SSE_EVENT_TYPE.QUESTION_CREATED]: QuestionPayload;
  [SSE_EVENT_TYPE.QUESTION_UPDATED]: QuestionPayload;
  [SSE_EVENT_TYPE.QUESTION_DELETED]: QuestionPayload;
  [SSE_EVENT_TYPE.QUESTION_MATCHES_UPDATED]: QuestionMatchesUpdatedPayload;
  [SSE_EVENT_TYPE.INTERACTION_CHANGED]: InteractionPayload;
  [SSE_EVENT_TYPE.FEED_NEW_TOP_POST]: FeedNewTopPostPayload;
  [SSE_EVENT_TYPE.WE_MET_CHANGED]: WeMetChangedPayload;
  [SSE_EVENT_TYPE.NOTIFICATION_CREATED]: Record<string, unknown>;
  [SSE_EVENT_TYPE.NOTIFICATION_UPDATED]: Record<string, unknown>;
  [SSE_EVENT_TYPE.ROOM_UPDATED]: RoomUpdatedPayload;
  [SSE_EVENT_TYPE.CHAT_MESSAGE]: ChatMessagePayload;
  [SSE_EVENT_TYPE.SPACE_UPDATED]: Record<string, unknown>;
  [SSE_EVENT_TYPE.SPACE_MEMBER_JOINED]: SpaceMemberPayload;
  [SSE_EVENT_TYPE.SPACE_MEMBER_LEFT]: SpaceMemberPayload;
  [SSE_EVENT_TYPE.SPACE_MEMBER_STATUS_CHANGED]: SpaceMemberPayload;
  [SSE_EVENT_TYPE.SPACE_MEMBER_REMOVED]: SpaceMemberPayload;
  [SSE_EVENT_TYPE.SPACE_TOPIC_CHANGED]: SpaceTopicChangedPayload;
  [SSE_EVENT_TYPE.SPACE_QUESTION_CREATED]: SpaceQuestionCreatedPayload;
  [SSE_EVENT_TYPE.CALENDAR_SYNC_CHANGED]: CalendarSyncChangedPayload;
  [SSE_EVENT_TYPE.EVENT_CREATED]: EventEntityPayload;
  [SSE_EVENT_TYPE.EVENT_UPDATED]: EventEntityPayload;
  [SSE_EVENT_TYPE.EVENT_DELETED]: EventEntityPayload;
  [SSE_EVENT_TYPE.EVENT_WENT_LIVE]: EventClockPayload;
  [SSE_EVENT_TYPE.EVENT_ENDED]: EventClockPayload;
  [SSE_EVENT_TYPE.USER_UPDATED]: UserEntityPayload;
  [SSE_EVENT_TYPE.HELPER_MATCH_CREATED]: HelperMatchPayload;
  [SSE_EVENT_TYPE.FULL_SYNC]: Record<string, never>;
  [SSE_EVENT_TYPE.CONNECTED]: Record<string, unknown>;
}

/** Discriminated union of all known SSE events. When narrowed via
 *  `event.type` in a switch, TypeScript knows the exact shape of
 *  `event.data` for that case. `useEventStream` validates incoming events
 *  against `SSE_EVENT_TYPE` and drops unknown types before dispatching, so
 *  consumers never see an unknown event and `switch` over this union can
 *  be exhaustive. Consumers should pair the switch with an `assertNever`
 *  call in the `default` branch so a contributor adding a new
 *  `SSE_EVENT_TYPE` value without wiring a handler is caught at compile
 *  time and surfaces loudly at runtime. */
export type SSEEvent = {
  [K in SSEEventType]: { type: K; data: SSEEventDataMap[K]; id?: string };
}[SSEEventType];

/** Runtime guard: is this string a known SSE event type? */
const KNOWN_SSE_EVENT_TYPES: ReadonlySet<string> = new Set(Object.values(SSE_EVENT_TYPE));

export function isSSEEventType(value: string): value is SSEEventType {
  return KNOWN_SSE_EVENT_TYPES.has(value);
}
